// @flow
import React, { Component, Fragment } from 'react';
import type { Node } from 'react';
import PropTypes from 'prop-types';

// Helpers
import classNames from 'classnames';
import { gtmEventPush } from 'utils/gtm/events';
import { formatExternalLink } from 'utils/utmLinks';

import Icon from 'Common/Icon';

import styles from 'components/Card.scss';
import Picture from 'Common/Picture';
import Image from 'Common/Image';
import Button from 'Common/Button';
import isWindow from 'Common/isWindow';
import LightBox from 'Common/LightBox';
import SimpleModal from 'Common/SimpleModal';
import PriceIndicator from 'Common/PriceIndicator';
import { createPortal } from 'react-dom';
import Gallery from 'Common/Gallery';
import PartnershipLogo from 'Common/PartnershipLogo';

/**
 * <Card /> component.
 * @return {class} returns card instance
 */
class Card extends Component<*, *> {
  /**
   * @param {object} props - props passed
   */
  constructor(props: Object) {
    super(props);
    this.state = {
      imageSrc: this.props.image,
      showDealerImage: true,
      enablePriceIndicatorInfo: false
    };
  }

  /**
   * Implements propTypes().
   *
   * @return {Object} - propType object.
   * @prop {string} id - card id.
   * @prop {string} name - card name.
   * @prop {number} price - card price.
   * @prop {string} year - card year.
   * @prop {oneOfType} distanceOrTown - card distanceOrTown.
   * @prop {number} mileage - card mileage.
   * @prop {string} fuelType - card fuelType.
   * @prop {number} doors - card doors.
   * @prop {string} transmission - card transmission.
   * @prop {string} image - card image.
   * @prop {string} priceIndicatorRating - priceIndicatorRating.
   * @prop {array} carousel - images list.
   * @prop {object} history - history.
   * @prop {object} item - item.
   * @prop {func} handleClick: handleClick
   * @prop {bool} isMobile - isMobile.
   * @prop {string} attentionGrabber - attentionGrabber.
   * @prop {number} enginePower - enginePower.
   * @prop {number} engineSize - engineSize.
   */
  static propTypes = {
    id: PropTypes.string,
    name: PropTypes.string,
    make: PropTypes.string,
    range: PropTypes.string,
    price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    year: PropTypes.string,
    distanceOrTown: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    mileage: PropTypes.number,
    fuelType: PropTypes.string,
    doors: PropTypes.number,
    transmission: PropTypes.string,
    url: PropTypes.string,
    image: PropTypes.object,
    priceIndicatorRating: PropTypes.string,
    carousel: PropTypes.array,
    dealerImage: PropTypes.string,
    cardLayout: PropTypes.string,
    history: PropTypes.object,
    item: PropTypes.object,
    handleClick: PropTypes.func,
    network: PropTypes.string,
    gcLid: PropTypes.string,
    gtmSectionName: PropTypes.string.isRequired,
    isMobile: PropTypes.bool,
    attentionGrabber: PropTypes.string,
    engineSize: PropTypes.number,
    enginePower: PropTypes.number
  };

  /**
   * Implements defaultProps().
   */
  static defaultProps: Object = {
    cardLayout: 'horizontal',
    priceIndicatorRating: ''
  };

  setFallbackDealerLogo: () => void = () => {
    this.setState({
      showDealerImage: false
    });
  };

  setQuickLook: (e: SyntheticEvent<HTMLButtonElement>) => void = e => {
    // $FlowFixMe
    document.body.style.overflow = 'unset';
    this.setState({
      enableQuickLook: false
    });
  };

  setPriceIndicatorInfoLook: (
    e: SyntheticEvent<HTMLButtonElement>
  ) => void = e => {
    // $FlowFixMe
    document.body.style.overflow = 'unset';
    this.setState({
      enablePriceIndicatorInfo: false
    });
  };

  handleClick: (e: SyntheticEvent<HTMLButtonElement>) => void = e => {
    const { item, handleClick } = this.props;
    e.preventDefault();
    handleClick(item);
    const url = this.generateUrl();
    isWindow && window.open(url, '_blank');
  };

  handleClickChild: (
    e: SyntheticEvent<HTMLButtonElement>,
    name: string
  ) => void = (e, name) => {
    // Stop the parent onClick from happening too.
    e.stopPropagation();
    gtmEventPush({
      method: this.props.gtmSectionName,
      args: {
        itemLabel: `Result page - ${name}`
      }
    });
    this.handleClick(e);
  };

  generateUrl: () => string = () => {
    const { network, gcLid, url } = this.props;
    const linkParams = {
      link: url,
      network,
      gcLid
    };
    const uri = formatExternalLink(linkParams);
    return uri;
  };

  createLightbox: any = (portalRef: any, externalUrl) => {
    if (portalRef && portalRef.current) {
      const lightbox = (
        <LightBox
          onClose={this.setQuickLook}
          {...this.props}
          externalUrl={externalUrl}
        />
      );
      return createPortal(lightbox, portalRef.current);
    } else {
      return null;
    }
  };

  /**
   * Renders <Card /> component.
   *
   * @return {JSX} - renders a logo.
   */
  render(): Node {
    const {
      name,
      price,
      year,
      distanceOrTown,
      mileage,
      fuelType,
      doors,
      transmission,
      image,
      priceIndicatorRating,
      isMobile,
      portalRef,
      carousel
    } = this.props;
    const { enableQuickLook, enablePriceIndicatorInfo } = this.state;
    const externalUrl = this.generateUrl();

    const SpecificationList = (
      <ul className={styles.specificationList}>
        {year && (
          <li className={styles.detail} data-fuel-type>
            {year} reg
          </li>
        )}
        {fuelType && (
          <li className={styles.detail} data-fuel-type>
            {fuelType}
          </li>
        )}
        {doors && (
          <li className={styles.detail} data-fuel-type>
            {doors} Doors
          </li>
        )}
        {transmission && (
          <li className={styles.detail} data-fuel-type>
            {transmission !== 'N/A'
              ? transmission[0].toUpperCase() +
                transmission.substring(1).toLowerCase()
              : transmission}
          </li>
        )}
        {mileage && (
          <li className={styles.detail} data-fuel-type>
            {mileage && mileage.toLocaleString('en')} Miles
          </li>
        )}
      </ul>
    );

    const displayButton = !isMobile ? (
      <Button
        extendStyle={styles.button}
        buttonText={'Quick look'}
        onClick={e => {
          e.stopPropagation();
          e.preventDefault();
          this.setState({ enableQuickLook: !enableQuickLook });
          return false;
        }}
      />
    ) : (
      <Button
        type={isMobile ? 'primary' : 'transparent'}
        rel="nofollow"
        extendStyle={styles.externalButton}
        buttonText={
          <Fragment>
            Full vehicle details
            <Icon
              className={classNames(styles.externalIcon)}
              iconName={'newwindow'}
              size={'default'}
            />
          </Fragment>
        }
        onClick={e => this.handleClickChild(e, name)}
      />
    );

    return (
      <div className={classNames(styles.card, styles.horizontalDetails)}>
        {image && !isMobile && (
          <div className={styles.image}>
            <Picture
              pictures={{
                ...image,
                title: name
              }}
            />
          </div>
        )}
        {!image && (
          <div className={styles.image}>
            <Image
              src={`${process.env.PUBLIC_URL || ''}/images/fallback.png`}
              alt={'Image unavailable'}
              title={'Image unavailable'}
            />
          </div>
        )}
        {isMobile && carousel && carousel.length ? (
          <Gallery pictures={carousel} />
        ) : null}

        <div className={classNames(styles.mainDetail, styles.detailsWrapper)}>
          <div className={classNames(styles.section, styles.title)}>
            <h3 className={styles.name}>{name}</h3>
            <div
              className={classNames(styles.detail, styles.detailDistanceOrTown)}
            >
              {distanceOrTown && (
                <Icon
                  className={classNames(styles.icon, styles.iconDistanceOrTown)}
                  iconName={'location'}
                  size={'medium'}
                />
              )}
              {distanceOrTown}
            </div>
          </div>
          <div className={classNames(styles.section, styles.specs)}>
            {SpecificationList}
          </div>
          <div className={classNames(styles.section, styles.priceWrapper)}>
            <div className={styles.price}>
              <div className={styles.priceIndicatorRating}>
                {!!priceIndicatorRating &&
                  priceIndicatorRating !== 'NOANALYSIS' && (
                    <div className={styles.priceIndicator}>
                      <Icon
                        iconName={`AT-${priceIndicatorRating}`}
                        size={'large'}
                        className={styles.rating}
                      />
                      <Icon
                        iconName={'info'}
                        size={'medium'}
                        onClick={e => {
                          e.stopPropagation();
                          e.preventDefault();
                          this.setState({
                            enablePriceIndicatorInfo: !enablePriceIndicatorInfo
                          });
                        }}
                        className={styles.info}
                      />
                    </div>
                  )}
              </div>
              <div className={styles.priceText}>{price}</div>
            </div>
            <div className={styles.atLink}>{displayButton}</div>
            {isMobile && <PartnershipLogo extendStyle={styles.partnerLogo} />}
          </div>
          {!isMobile && (
            <div className={classNames(styles.section, styles.fullVehicle)}>
              <Button
                type={isMobile ? 'primary' : 'transparent'}
                rel="nofollow"
                extendStyle={classNames(
                  styles.fullVehicleButton,
                  styles.externalButton
                )}
                buttonText={
                  <Fragment>
                    Full vehicle details
                    <Icon
                      className={classNames(styles.externalIcon)}
                      iconName={'newwindow'}
                      size={'default'}
                    />
                  </Fragment>
                }
                onClick={e => this.handleClickChild(e, name)}
              />
            </div>
          )}
        </div>
        {enableQuickLook &&
          !isMobile &&
          this.createLightbox(portalRef, externalUrl)}
        {enablePriceIndicatorInfo && (
          <SimpleModal
            closeIcon={'close'}
            onClose={this.setPriceIndicatorInfoLook}
            isModalOpen={enablePriceIndicatorInfo}
          >
            <button
              className={styles.closeButton}
              onClick={this.setPriceIndicatorInfoLook}
            >
              Close
            </button>
            <PriceIndicator />
          </SimpleModal>
        )}
      </div>
    );
  }
}

export default Card;



// WEBPACK FOOTER //
// ./src/Common/Card/index.js